import { ExternalLinkIcon, FileTextIcon } from 'lucide-react'

import { Proposal } from '../../types'
import { USD_MOCK_TOKEN } from '@/domain/types/Token'
import GreenPulseCircleIcon from '@/ui/assets/green-pulse-circle.svg?react'
import { ButtonIcon } from '@/ui/atoms/button/Button'
import { ExternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { Panel } from '@/ui/atoms/panel/Panel'
import { links } from '@/ui/constants/links'
import { AmountCell } from '@/ui/molecules/data-table/components/AmountCell'
import { DateCell } from '@/ui/molecules/data-table/components/DateCell'
import { ResponsiveDataTable } from '@/ui/organisms/responsive-data-table/ResponsiveDataTable'
import { testIds } from '@/ui/utils/testIds'

export interface ActiveProposalsPanelProps {
  proposals: Proposal[]
}

export function ActiveProposalsPanel({ proposals }: ActiveProposalsPanelProps) {
  if (proposals.length === 0) {
    return <NoActiveProposals />
  }
  return <ActiveProposals proposals={proposals} />
}

export function ActiveProposals({ proposals }: ActiveProposalsPanelProps) {
  return (
    <Panel spacing="m" className="flex flex-col gap-9">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="typography-heading-6 sm:typography-heading-5 flex items-center gap-1">
          Active proposals <GreenPulseCircleIcon className="icon-size-sm mt-1 hidden sm:block" />
        </div>
        <ExternalLinkButton to={links.snapshot.proposals} variant="tertiary">
          View on Snapshot <ExternalLinkIcon className="icon-size-xs" />
        </ExternalLinkButton>
      </div>
      <ResponsiveDataTable
        gridTemplateColumnsClassName="grid-cols-[4fr_1fr_1fr_1fr_140px]"
        data={proposals}
        data-testid={testIds.spkGovernance.activeProposalsPanel.table}
        columnDefinition={{
          title: {
            header: 'Title',
            renderCell: ({ title }) => (
              <div className="flex items-center gap-2">
                <div className="rounded-full bg-secondary p-2">
                  <FileTextIcon className="icon-size-xs icon-primary" />
                </div>
                <div
                  className="typography-label-3 text-primary"
                  data-testid={testIds.spkGovernance.activeProposalsPanel.title}
                >
                  {title}
                </div>
              </div>
            ),
          },
          votes: {
            header: 'Votes',
            headerAlign: 'right',
            renderCell: ({ votes }, mobileViewOptions) => {
              return (
                <AmountCell
                  token={USD_MOCK_TOKEN}
                  amount={votes}
                  mobileViewOptions={mobileViewOptions}
                  className="typography-label-3"
                  data-testid={testIds.spkGovernance.activeProposalsPanel.votes}
                  formattingOptions={{
                    showUsdValue: false,
                    style: 'compact',
                  }}
                />
              )
            },
          },
          start: {
            header: 'Start date',
            headerAlign: 'right',
            renderCell: ({ start }, mobileViewOptions) => (
              <DateCell
                value={start}
                mobileViewOptions={mobileViewOptions}
                data-testid={testIds.spkGovernance.activeProposalsPanel.start}
              />
            ),
          },
          end: {
            header: 'End date',
            headerAlign: 'right',
            renderCell: ({ end }, mobileViewOptions) => (
              <DateCell
                value={end}
                mobileViewOptions={mobileViewOptions}
                data-testid={testIds.spkGovernance.activeProposalsPanel.end}
              />
            ),
          },
          action: {
            header: 'Action',
            headerAlign: 'right',
            renderCell: ({ voteLink }) => (
              <div className="flex justify-end sm:pl-10">
                <ExternalLinkButton
                  to={voteLink}
                  variant="secondary"
                  className="w-full"
                  size="s"
                  data-testid={testIds.spkGovernance.activeProposalsPanel.link}
                >
                  Vote <ButtonIcon icon={ExternalLinkIcon} />
                </ExternalLinkButton>
              </div>
            ),
          },
        }}
      />
    </Panel>
  )
}

function NoActiveProposals() {
  return (
    <Panel spacing="m" className="flex flex-col gap-9">
      <div className="my-auto flex flex-col items-center gap-5">
        <div className="rounded-full bg-secondary p-3">
          <FileTextIcon className="icon-size-md icon-primary" />
        </div>
        <div className="flex flex-col items-center gap-2 text-center">
          <div
            className="typography-heading-5 text-primary"
            data-testid={testIds.spkGovernance.noActiveProposalsPanel.message}
          >
            Currently, there are no <span className="whitespace-nowrap">active proposals</span>
          </div>
          <div
            className="typography-body-3 text-secondary"
            data-testid={testIds.spkGovernance.noActiveProposalsPanel.description}
          >
            Follow Spark on Snapshot to keep up to date <span className="whitespace-nowrap">with new proposals.</span>
          </div>
        </div>
        <ExternalLinkButton
          to={links.snapshot.sparkSpace}
          variant="tertiary"
          data-testid={testIds.spkGovernance.noActiveProposalsPanel.followSnapshotButton}
        >
          Follow on Snapshot <ExternalLinkIcon className="icon-size-sm" />
        </ExternalLinkButton>
      </div>
    </Panel>
  )
}
